import { Link } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import api from "../Services/api";
import { useAuth } from "../Contexts/AuthContext";
import "../styles/attendance.css";

/* ── HELPER: initials ── */
const initials = (name) => {
  if (!name) return "??";
  return name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
};

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const AVATAR_COLORS = [
  ["#eef5d0","#4a6508"],
  ["#e8f4fd","#2a85c7"],
  ["#f2f7e4","#3a7000"],
  ["#fff8e6","#c47f0a"],
  ["#fef2f2","#c0392b"],
  ["#f3e8ff","#7c3aed"],
];

const isWorkingDay = (year, month, d, rule = '5_DAYS') => {
  const curDate = new Date(year, month, d);
  const day = curDate.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  
  if (rule === '7_DAYS') return true;
  
  // Sunday is off for all other rules
  if (day === 0) return false;
  
  if (day === 6) {
    // Saturday Logic
    if (rule === '6_DAYS') return true;
    
    // Count which Saturday of the month this is
    let satCount = 0;
    for (let i = 1; i <= d; i++) {
      if (new Date(year, month, i).getDay() === 6) satCount++;
    }
    
    if (rule === '2ND_SAT_OFF') return satCount !== 2;
    if (rule === '2ND_4TH_SAT_OFF') return satCount !== 2 && satCount !== 4;
    if (rule === 'ALL_SAT_OFF' || rule === '5_DAYS') return false;
  }
  
  // Mon-Fri
  return true;
};

const getWorkingDays = (year, month, rule = '5_DAYS') => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  let count = 0;
  for (let d = 1; d <= daysInMonth; d++) {
    if (isWorkingDay(year, month, d, rule)) count++;
  }
  return count;
};

const fmtINR = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(Number(amount) || 0));

const STATUS_CHIP = {
  Good:    { bg: "#edfade", color: "#2e7000", border: "#aadb6e" },
  Average: { bg: "#fff8e6", color: "#c47f0a", border: "#f5c842" },
  Low:     { bg: "#fff0f0", color: "#e53935", border: "#ffbbbb" },
};

export default function Attendance() {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState("personal"); // 'personal', 'team', or 'report'
  const [records, setRecords] = useState([]);
  const [personalHistory, setPersonalHistory] = useState([]);
  const [selectedDate, setSelectedDate] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Monthly Report States
  const [repMonth, setRepMonth] = useState(new Date().getMonth());
  const [repYear, setRepYear] = useState(new Date().getFullYear());
  const [monthlyRecords, setMonthlyRecords] = useState([]);
  const [monthlyLeaves, setMonthlyLeaves] = useState([]);
  const [orgSettings, setOrgSettings] = useState({ work_week_rule: '5_DAYS' });


  const canViewTeam = user?.is_superuser || user?.all_permissions?.includes("view_attendance") || user?.all_permissions?.includes("manage_attendance");
  const canManage = user?.is_superuser || user?.all_permissions?.includes("manage_attendance");

  const fetchAttendance = useCallback(async () => {
    try {
      const response = await api.get(`hr/attendance/?date=${selectedDate}`);
      
      const data = Array.isArray(response.data) ? response.data : response.data.results || [];
      setRecords(data);
    } catch (error) {
      console.error("Failed to fetch attendance:", error);
    }
  }, [selectedDate]);

  const fetchPersonalHistory = useCallback(async () => {
    try {
      const response = await api.get(`hr/attendance/`);
      const rawData = Array.isArray(response.data) ? response.data : response.data.results || [];
      // Deduplicate by date, preferring 'Present' status if multiple exist
      const uniqueData = rawData.reduce((acc, current) => {
        const existing = acc.find(item => item.date === current.date);
        if (!existing) return acc.concat([current]);
        if (current.status === 'Present') {
          return acc.map(item => item.date === current.date ? current : item);
        }
        return acc;
      }, []);
      setPersonalHistory(uniqueData.slice(0, 10)); // Last 10 records
    } catch (error) {
      console.error("Failed to fetch personal history:", error);
    }
  }, []);

  const fetchMonthlyReport = useCallback(async () => {
    if (!canViewTeam) return;
    try {
      const recordsUrl = `hr/attendance/?month=${repMonth + 1}&year=${repYear}`;
      const leavesUrl = `hr/leaves/?month=${repMonth + 1}&year=${repYear}&status=Approved`;
      
      const [recRes, leafRes, orgRes] = await Promise.all([
        api.get(recordsUrl),
        api.get(leavesUrl),
        api.get('organizations/me/')
      ]);
      
      const recData = Array.isArray(recRes.data) ? recRes.data : recRes.data.results || [];
      const leafData = Array.isArray(leafRes.data) ? leafRes.data : leafRes.data.results || [];
      
      setMonthlyRecords(recData);
      setMonthlyLeaves(leafData);
      setOrgSettings(orgRes.data);
    } catch (error) {
      console.error("Failed to fetch monthly report data:", error);
    }
  }, [canViewTeam, repMonth, repYear]);

  useEffect(() => {
    if (viewMode === "team" && canViewTeam) {
      fetchAttendance();
    } else if (viewMode === "report" && canViewTeam) {
      fetchMonthlyReport();
    } else {
      fetchPersonalHistory();
      fetchAttendance(); 
    }
  }, [viewMode, fetchAttendance, fetchPersonalHistory, fetchMonthlyReport, canViewTeam]);

  const handleCheckIn = async () => {
    try {
      await api.post(`hr/attendance/check_in/`, {});
      fetchAttendance();
      fetchPersonalHistory();
    } catch (error) {
      alert("Failed to check in: " + (error.response?.data?.error || "Error"));
    }
  };

  const handleCheckOut = async () => {
    try {
      await api.post(`hr/attendance/check_out/`, {});
      fetchAttendance();
      fetchPersonalHistory();
      if (viewMode === "report") fetchMonthlyReport();
    } catch (error) {
      alert("Failed to check out: " + (error.response?.data?.error || "Error"));
    }
  };

  const handleMarkAttendance = async (status = "Present", targetUserId = null, recordId = null) => {
    try {
      const today = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;
      const actualTargetUserId = targetUserId || user.id;
      
      const targetRecord = recordId ? { id: recordId } : records.find(r => r.user_details.id === actualTargetUserId && r.date === today);
      
      if (targetRecord) {
        await api.patch(`hr/attendance/${targetRecord.id}/`, { status });
      } else {
        await api.post(`hr/attendance/`, { user: actualTargetUserId, status, date: today });
      }
      fetchAttendance();
      fetchPersonalHistory();
      if (viewMode === "report") fetchMonthlyReport();
    } catch (error) {
      alert("Failed to update attendance.");
    }
  };

  const processMonthlyData = () => {
    const now = new Date();
    const isCurrentMonth = now.getMonth() === repMonth && now.getFullYear() === repYear;
    const totalDaysInMonth = new Date(repYear, repMonth + 1, 0).getDate();
    const workingDays = getWorkingDays(repYear, repMonth, orgSettings.work_week_rule);
    
    let daysPassed = totalDaysInMonth; 
    if (isCurrentMonth) {
      daysPassed = now.getDate();
    } else if (repYear > now.getFullYear() || (repYear === now.getFullYear() && repMonth > now.getMonth())) {
      daysPassed = 0;
    }
    const remainingDays = totalDaysInMonth - daysPassed;

    // 1. Group records and leaves by user for quick access
    const userAttendanceMap = {};
    monthlyRecords.forEach(r => {
        const uid = r.user_details.id;
        if(!userAttendanceMap[uid]) userAttendanceMap[uid] = {};
        userAttendanceMap[uid][r.date] = r.status;
    });

    const userLeavesMap = {};
    monthlyLeaves.forEach(l => {
        const uid = l.user_details.id;
        if(!userLeavesMap[uid]) userLeavesMap[uid] = [];
        userLeavesMap[uid].push(l);
    });

    // 2. Perform Day-by-Day Audit for every user mentioned in records or leaves
    const uniqueUserIds = [...new Set([...monthlyRecords.map(r => r.user_details.id), ...monthlyLeaves.map(l => l.user_details.id)])];
    
    return uniqueUserIds.map(uId => {
      const uDetails = (monthlyRecords.find(r => r.user_details.id === uId) || monthlyLeaves.find(l => l.user_details.id === uId)).user_details;
      
      let p = 0, a = 0, l = 0, ul = 0;
      
      // Audit every day from 1 to daysPassed
      for (let d = 1; d <= daysPassed; d++) {
        const dateStr = `${repYear}-${String(repMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        const status = userAttendanceMap[uId]?.[dateStr];
        
        if (status === "Present") {
          p++;
        } else if (status === "Absent") {
          a++;
        } else if (status === "Paid Leave") {
          l++;
        } else if (status === "Holiday") {
          // Holiday - Paid in inclusive model, not counted as absent
        } else {
          // No explicit attendance record. Check for Leaves or Weekend status.
          const activeLeave = (userLeavesMap[uId] || []).find(lv => dateStr >= lv.start_date && dateStr <= lv.end_date);
          if (activeLeave) {
            l++;
            if (activeLeave.leave_type === 'Unpaid') ul++;
          } else {
            // Is it a working day?
            if (isWorkingDay(repYear, repMonth, d, orgSettings.work_week_rule)) {
               // Unmarked working day is treated as ABSENT if it has already passed
               a++;
            } else {
               // Weekend - Paid in Inclusive model
            }
          }
        }
      }

      const pct = workingDays > 0 ? Math.round((p / workingDays) * 100) : 0;
      const baseSalary = parseFloat(uDetails.base_salary) || 0;
      const dayRate = baseSalary / 30;

      let earnedSalary = 0;
      if (orgSettings.salary_basis === 'WORKING_DAYS_ONLY') {
        // Exclusive model: Pay = (Present + Paid Leave) / total Working Days
        const paidDays = p + (l - ul);
        earnedSalary = workingDays > 0 ? Math.round((paidDays / workingDays) * baseSalary) : 0;
      } else {
        // netEarnedDays = (daysPassed - absent - unpaidLeave)
        const netEarnedDays = daysPassed - (a + ul);
        earnedSalary = Math.round(netEarnedDays * dayRate);
        earnedSalary = Math.min(baseSalary, Math.max(0, earnedSalary));
      }

      return {
        ...uDetails,
        workingDays,
        presentDays: p,
        absentDays: a,
        leaveDays: l,
        unpaidLeaveDays: ul,
        remainingDays,
        pct,
        earnedSalary,
        statusLabel: pct >= 85 ? "Good" : pct >= 70 ? "Average" : "Low"
      };
    });
  };

  const monthlyData = processMonthlyData();
  const myTodayRecord = records.find(r => r.user_details.id === user.id && r.date === `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`);

  const handleExport = () => {
    const rows = [
      ["Employee","Department","Base Salary (₹)","Working Days","Present Days","Absent Days","Leave Days","Earned Salary (₹)","Attendance %","Status"],
      ...monthlyData.map((r) => [
        r.name || r.username, r.department || "N/A", r.base_salary, r.workingDays,
        r.presentDays, r.absentDays, r.leaveDays || 0,
        r.earnedSalary, `${r.pct}%`, r.statusLabel,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(blob),
      download: `attendance_salary_${MONTH_NAMES[repMonth]}_${repYear}.csv`,
    });
    a.click();
  };

  return (
    <div className="attendance-page-wrapper">
      <div className="page-head">
        <div>
          <div className="page-title">📊 Attendance Management</div>
          <div className="page-sub">Track your time and oversee your team efficiently.</div>
        </div>
        {canViewTeam && (
          <div className="tab-switcher">
            <button className={viewMode === "personal" ? "active" : ""} onClick={() => setViewMode("personal")}>My Attendance</button>
            <button className={viewMode === "team" ? "active" : ""} onClick={() => setViewMode("team")}>Team Records</button>
            <button className={viewMode === "report" ? "active" : ""} onClick={() => setViewMode("report")}>Salary Report</button>
          </div>
        )}
      </div>

      {viewMode === "personal" && (
        <div className="personal-view-grid">
          <div className="mark-card">
            <div className="mark-status">
              <span className="date-label">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
              <h3>Welcome back, {user?.first_name || user?.username}!</h3>
              <div className={`status-badge-large ${myTodayRecord?.status.toLowerCase() || 'none'}`}>
                {myTodayRecord?.status || "Not Checked In"}
              </div>
              
              {(myTodayRecord?.check_in || myTodayRecord?.check_out) && (
                <div className="shift-times" style={{ marginTop: "15px", display: "flex", gap: "20px", background: "#f8fafc", padding: "12px", borderRadius: "10px" }}>
                    {myTodayRecord.check_in && (
                        <div>
                            <div style={{ fontSize: "10px", color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>In Time</div>
                            <div style={{ fontSize: "15px", fontWeight: 800, color: "var(--green)" }}>{myTodayRecord.check_in.slice(0, 5)}</div>
                        </div>
                    )}
                    {myTodayRecord.check_out && (
                        <div>
                            <div style={{ fontSize: "10px", color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Out Time</div>
                            <div style={{ fontSize: "15px", fontWeight: 800, color: "#e53935" }}>{myTodayRecord.check_out.slice(0, 5)}</div>
                        </div>
                    )}
                </div>
              )}
            </div>
            
            <div className="mark-actions">
              {!myTodayRecord?.check_in ? (
                <button className="btn-mark present" style={{ width: "100%", justifyContent: "center" }} onClick={handleCheckIn}>
                  📍 Check In Now
                </button>
              ) : !myTodayRecord?.check_out ? (
                <button className="btn-mark absent" style={{ width: "100%", justifyContent: "center", background: "#f59e0b", borderColor: "#d97706" }} onClick={handleCheckOut}>
                  🏁 Check Out Now
                </button>
              ) : (
                <div style={{ width: "100%", textAlign: "center", padding: "10px", background: "var(--green-lt)", color: "var(--green-dk)", borderRadius: "8px", fontSize: "13px", fontWeight: 700 }}>
                  ✅ Shift Completed for today
                </div>
              )}
            </div>
          </div>


          <div className="history-card">
            <h3>Recent History</h3>
            <div className="history-list">
              {personalHistory.length === 0 ? (
                <div className="empty-state">No recent records found.</div>
              ) : (
                personalHistory.map(rec => (
                  <div key={rec.id} className="history-item">
                    <div className="history-info">
                        <span className="history-date">{rec.date}</span>
                        <div style={{ display: "flex", gap: "10px", fontSize: "11px", marginTop: "3px" }}>
                            <span style={{ color: "var(--green-dk)" }}>In: {rec.check_in ? rec.check_in.slice(0,5) : "--"}</span>
                            <span style={{ color: "#e53935" }}>Out: {rec.check_out ? rec.check_out.slice(0,5) : "--"}</span>
                        </div>
                    </div>
                    <span className={`history-status ${rec.status.toLowerCase()}`}>{rec.status}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {viewMode === "team" && (
        <>
          <div className="stats-row">
            <div className="stat-card">
              <div className="stat-ico all">👥</div>
              <div><div className="stat-num">{records.length}</div><div className="stat-label">Total Staff</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-ico here">✅</div>
              <div><div className="stat-num">{records.filter(r => r.status === "Present").length}</div><div className="stat-label">Present</div></div>
            </div>
            <div className="stat-card">
              <div className="stat-ico out">❌</div>
              <div><div className="stat-num">{records.filter(r => r.status === "Absent").length}</div><div className="stat-label">Absent</div></div>
            </div>
          </div>

          <div className="table-card">
            <div className="table-card-head">
              <div className="date-filter">
                <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} />
              </div>
              <div className="search-wrap">
                🔍 <input type="text" placeholder="Search employee..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
            </div>

            <div className="tbl-scroll">
              <table>
                <thead>
                  <tr><th>#</th><th>Employee</th><th>Department</th><th>Status</th><th>Log (In/Out)</th><th>Action</th></tr>
                </thead>
                <tbody>
                  {records.filter(r => r.user_details.username.toLowerCase().includes(searchTerm.toLowerCase()) || r.user_details.name.toLowerCase().includes(searchTerm.toLowerCase())).map((rec, i) => (
                    <tr key={rec.id}>
                      <td>{i+1}</td>
                      <td>
                        <div className="emp-cell">
                          <div className="emp-ava">{initials(rec.user_details.name)}</div>
                          <div>{rec.user_details.name}</div>
                        </div>
                      </td>
                      <td>{rec.user_details.department || "N/A"}</td>
                      <td><span className={`badge ${rec.status.toLowerCase()}`}>{rec.status}</span></td>
                      <td>
                        <div style={{ fontSize: "12px", display: "flex", flexDirection: "column" }}>
                            <span style={{ color: "var(--green-dk)", fontWeight: 700 }}>{rec.check_in ? rec.check_in.slice(0, 5) : "--"}</span>
                            <span style={{ color: "#e53935", fontSize: "10px" }}>{rec.check_out ? rec.check_out.slice(0, 5) : "--"}</span>
                        </div>
                      </td>
                      <td>
                        {canManage && (
                          <button className="tog" onClick={() => handleMarkAttendance(rec.status === "Present" ? "Absent" : "Present", rec.user_details.id, rec.id)}>
                            Toggle
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {viewMode === "report" && (
        <div className="report-section">
          <div className="report-header">
            <div className="report-title-block">
              <div className="report-eyebrow">HR Analytics</div>
              <div className="report-title">📅 Monthly Attendance & Salary Report</div>
            </div>
            <div className="report-controls">
              <div className="month-nav">
                <button className="month-nav-btn" onClick={() => { if (repMonth === 0) { setRepMonth(11); setRepYear(y => y - 1); } else setRepMonth(m => m - 1); }}>‹</button>
                <span className="month-nav-label">{MONTH_NAMES[repMonth]} {repYear}</span>
                <button className="month-nav-btn" onClick={() => { if (repMonth === 11) { setRepMonth(0); setRepYear(y => y + 1); } else setRepMonth(m => m + 1); }}>›</button>
              </div>
              <button className="export-btn" onClick={handleExport}>📥 Export CSV</button>
            </div>
          </div>

          <div className="report-summary-strip">
            <div className="rss-card">
              <div className="rss-ico">📆</div>
              <div><div className="rss-num">{getWorkingDays(repYear, repMonth, orgSettings.work_week_rule)}</div><div className="rss-lbl">Working Days</div></div>
            </div>
            <div className="rss-card">
              <div className="rss-ico" style={{ background: "#edfade" }}>✅</div>
              <div><div className="rss-num">{monthlyData.reduce((a, b) => a + b.presentDays, 0)}</div><div className="rss-lbl">Total Present</div></div>
            </div>
            <div className="rss-card">
              <div className="rss-ico" style={{ background: "var(--red-lt)" }}>❌</div>
              <div><div className="rss-num">{monthlyData.reduce((a, b) => a + b.absentDays, 0)}</div><div className="rss-lbl">Total Absent</div></div>
            </div>
            <div className="rss-card">
              <div className="rss-ico" style={{ background: "var(--yellow-lt)" }}>🏖️</div>
              <div><div className="rss-num">{monthlyData.reduce((a, b) => a + (b.leaveDays || 0), 0)}</div><div className="rss-lbl">Total Leaves</div></div>
            </div>
            <div className="rss-card salary-card">
              <div className="rss-ico" style={{ background: "#d0f0e2" }}>💰</div>
              <div><div className="rss-num">{fmtINR(monthlyData.reduce((a, b) => a + b.earnedSalary, 0))}</div><div className="rss-lbl">Total Payout</div></div>
            </div>
          </div>

          <div className="report-table-card">
            <div className="report-table-head">
              <h3>Employee-wise Breakdown — {MONTH_NAMES[repMonth]} {repYear}</h3>
              <div className="report-meta">
                Avg attendance: <span>{Math.round(monthlyData.reduce((a, b) => a + b.pct, 0) / (monthlyData.length || 1))}%</span> &nbsp;·&nbsp; {getWorkingDays(repYear, repMonth, orgSettings.work_week_rule)} working days
                &nbsp;·&nbsp; Total payout: <span>{fmtINR(monthlyData.reduce((a, b) => a + b.earnedSalary, 0))}</span>
              </div>
            </div>
            <div className="tbl-scroll">
              <table className="rt-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Employee</th>
                    <th className="center">Working Days</th>
                    <th className="center">Present</th>
                    <th className="center">Absent</th>
                    <th className="center">Leave</th>
                    <th className="center">Unpaid</th>
                    <th className="center">Future</th>
                    <th className="center">Attendance %</th>
                    <th className="center">Earned Salary</th>
                    <th className="center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.map((r, i) => {
                    const chipCfg = STATUS_CHIP[r.statusLabel] || STATUS_CHIP.Good;
                    const barColor = r.pct >= 85 ? "#52b300" : r.pct >= 70 ? "#e6a817" : "#e53935";
                    return (
                      <tr key={r.id}>
                        <td style={{ color: "#b4c878", fontFamily: "Nunito", fontWeight: 800, fontSize: 11.5 }}>
                          {String(i + 1).padStart(2, "0")}
                        </td>
                        <td>
                          <div className="rt-emp-cell">
                            <div className="rt-ava" style={{background: AVATAR_COLORS[i % AVATAR_COLORS.length][0], color: AVATAR_COLORS[i % AVATAR_COLORS.length][1]}}>
                              {initials(r.name)}
                            </div>
                            <div>
                                <div className="rt-emp-name">{r.name}</div>
                                <div className="rt-emp-dept">{r.department}</div>
                            </div>
                          </div>
                        </td>
                        <td className="center">{r.workingDays}</td>
                        <td className="center"><span className="rt-days-num present">{r.presentDays}</span></td>
                        <td className="center"><span className="rt-days-num absent">{r.absentDays}</span></td>
                        <td className="center"><span className="rt-days-num leave">{r.leaveDays || 0}</span></td>
                        <td className="center"><span className="rt-days-num unpaid" style={{ background: '#fee2e2', color: '#b91c1c' }}>{r.unpaidLeaveDays || 0}</span></td>
                        <td className="center" style={{ color: '#94a3b8', fontSize: '12px' }}>{r.remainingDays || 0}d</td>
                        <td className="center">
                          <div className="rt-pct-cell">
                            <div className="rt-mini-bar"><div className="rt-mini-fill" style={{ width: `${r.pct}%`, background: barColor }} /></div>
                            <span className="rt-pct-val" style={{ color: barColor }}>{r.pct}%</span>
                          </div>
                        </td>
                        <td className="center">
                          <div className="salary-cell">
                            <span className="salary-earned">{fmtINR(r.earnedSalary)}</span>
                            <span className="salary-base">of {fmtINR(r.base_salary)}</span>
                            <span className="salary-chip">
                              💼 {r.presentDays}/{r.workingDays} days · {r.pct}%
                            </span>
                          </div>
                        </td>
                        <td className="center">
                          <span className="rt-status-chip" style={{ background: chipCfg.bg, color: chipCfg.color, border: `1.5px solid ${chipCfg.border}` }}>
                            {r.statusLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {orgSettings.salary_basis === 'FIXED_30_DAYS' && (
              <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', fontSize: '0.85rem', color: '#475569', border: '1px solid #e2e8f0', lineHeight: '1.6' }}>
                <div style={{ fontWeight: 800, color: '#1e293b', marginBottom: '4px' }}>ℹ️ Proration Policy: Fixed 30-Day Model (Inclusive)</div>
                Earned Salary is calculated as: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>Base - ((Absent + Unpaid + Future) × Daily Rate)</code>
                <br />
                <span style={{ fontSize: '11px', color: '#64748b' }}>* Future days are days in the selected month that haven't passed yet (Unearned). Daily Rate = Base / 30.</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}